import { decomposeRotation, type View } from "@novorender/api";
import { quat, type ReadonlyQuat, type ReadonlyVec3, vec3 } from "gl-matrix";

import { radToDeg } from "utils/math";

import { InvertedFlightController } from "./invertedFlightController";

type FlightOrientation = { roll: number; rotation: ReadonlyQuat };

type FlightPivot = {
    center: ReadonlyVec3;
    offset: ReadonlyVec3;
    distance: number;
    active: boolean;
};

type FlightInternals = {
    _orientation: FlightOrientation;
    _pivot?: FlightPivot;
};

type Transformations = {
    tx: number;
    ty: number;
    tz: number;
    rx: number;
    ry: number;
    shouldPivot: boolean;
};

/**
 * Default (`flight`) controller. Same inverted-look flag as {@link InvertedFlightController}, plus
 * optional roll preservation for SpaceMouse Object-mode past vertical.
 *
 * `keepRoll` stays off unless a live SpaceMouse session turns it on, so ordinary Default navigation
 * still self-levels the way the base `FlightController` does.
 */
export class SpaceMouseFlightController extends InvertedFlightController {
    keepRoll = false;

    override init(params: Parameters<InvertedFlightController["init"]>[0]): void {
        super.init(params);
        if (!this.keepRoll) {
            return;
        }
        const { position, rotation } = params;
        if (rotation) {
            this.moveTo(position ?? this.position, 0, rotation);
        }
    }

    /**
     * App fly-tos (Home, bookmarks, viewpoints) only animate pitch/yaw. Force roll to 0 so
     * `restoreRoll` cannot keep an Object-mode inversion — and so a destination quat that
     * decomposes to a non-zero roll (typical of look-at / default-camera poses) cannot land
     * tilted. Ordinary Default discards that roll on `init` / `update`; timed fly-tos must too.
     * Instant `moveTo(..., 0)` still applies the full quat (unpark / keep-roll restore).
     */
    override moveTo(
        targetPosition: ReadonlyVec3,
        flyTime = 1000,
        rotation?: ReadonlyQuat,
        easeFunction?: (t: number) => number,
    ): void {
        super.moveTo(targetPosition, flyTime, rotation, easeFunction);
        if (this.keepRoll && flyTime) {
            this.internals()._orientation.roll = 0;
        }
    }

    /**
     * `zoomTo` never goes through `moveTo`. Flatten the same way so fly-to-selected cannot keep
     * an inverted pose. Super runs first so the dolly target is still computed from the current look.
     */
    override zoomTo(boundingSphere: Parameters<InvertedFlightController["zoomTo"]>[0], flyTime = 1000): void {
        super.zoomTo(boundingSphere, flyTime);
        if (this.keepRoll && flyTime) {
            this.internals()._orientation.roll = 0;
        }
    }

    override update(): void {
        if (!this.keepRoll) {
            super.update();
            return;
        }
        if (this.currentFlyTo) {
            super.update();
            return;
        }
        const { roll } = decomposeRotation(this.rotation);
        if (Math.abs(roll) < 1e-4) {
            super.update();
            return;
        }
        const orbiting = this.isOrbiting();
        super.update();
        this.restoreRoll(roll);
        this.fixupPivot(orbiting);
    }

    /**
     * Base update zeroes roll, then translates/looks in that unrolled frame. Past vertical the
     * displayed pose is inverted, so unrolled A/D and LMB disagree with the screen. Flip those axes
     * when the kept roll is past 90°.
     */
    protected override getTransformations(): Transformations {
        const t = super.getTransformations() as Transformations;
        if (!this.keepRoll) {
            return t;
        }
        const { roll } = decomposeRotation(this.rotation);
        if (Math.abs(roll) <= Math.PI / 2) {
            return t;
        }
        return { ...t, tx: -t.tx, ty: -t.ty, rx: -t.rx, ry: -t.ry };
    }

    private internals(): FlightInternals {
        // FlightController keeps roll/pivot private and restoreRoll must write them without
        // `changed()`, or idle frames never settle. There is no public keep-roll API.
        return this as unknown as FlightInternals;
    }

    /** RMB / three-finger drag is orbit; capture before `update` consumes the axes. */
    private isOrbiting(): boolean {
        const { mouse_rmb_move_x, mouse_rmb_move_y, touch_3_move_x, touch_3_move_y } = this.input.axes;
        return mouse_rmb_move_x !== 0 || mouse_rmb_move_y !== 0 || touch_3_move_x !== 0 || touch_3_move_y !== 0;
    }

    /** FlightController.update assigns `roll = 0`. Put it back without `changed()`, or idle
     *  frames would keep calling update and never settle. */
    private restoreRoll(rollRad: number): void {
        this.internals()._orientation.roll = radToDeg(rollRad);
    }

    /**
     * Orbit placed the camera with roll stripped. Reseat on the restored arm so the first RMB does
     * not teleport. A pan/zoom with the pivot held must not move the camera to satisfy that arm —
     * rebuild the offset for the restored rotation instead.
     */
    private fixupPivot(orbiting: boolean): void {
        const pivot = this.internals()._pivot;
        if (!pivot?.active) {
            return;
        }
        if (orbiting) {
            this.reseatOrbitPivot(pivot);
            return;
        }
        this.internals()._pivot = computePivot(pivot.center, pivot.active, this.position, this.rotation);
    }

    private reseatOrbitPivot(pivot: FlightPivot): void {
        const arm = vec3.fromValues(0, 0, pivot.distance);
        vec3.add(arm, arm, pivot.offset);
        vec3.transformQuat(arm, arm, this.rotation);
        const next = vec3.add(vec3.create(), pivot.center, arm);
        if (vec3.distance(this.position, next) < 1e-6) {
            return;
        }
        this.position = next;
    }
}

/** Same math as Novorender's `computePivot`: camera-space offset of `center` at this pose. */
function computePivot(
    center: ReadonlyVec3,
    active: boolean,
    position: ReadonlyVec3,
    rotation: ReadonlyQuat,
): FlightPivot {
    const distance = vec3.distance(center, position);
    const offset = vec3.fromValues(0, 0, distance);
    vec3.transformQuat(offset, offset, rotation);
    vec3.add(offset, center, offset);
    vec3.sub(offset, position, offset);
    vec3.transformQuat(offset, offset, quat.invert(quat.create(), rotation));
    return { center, offset, distance, active };
}

/** Turn Default keep-roll on only while a SpaceMouse session is live. No-ops if this class is not installed. */
export function setSpaceMouseKeepRoll(view: View | undefined, on: boolean): void {
    const flight = view?.controllers.flight;
    if (flight instanceof SpaceMouseFlightController) {
        flight.keepRoll = on;
    }
}
